import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  MapPin, Users, RefreshCw, Navigation, Phone, Mail, 
  Briefcase, Star, Clock, ExternalLink, Maximize2, Minimize2,
  Zap, Target, ChevronRight
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, ZoomControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Freshness helper: returns { label, color, dotColor } based on how recent the update was
const getFreshness = (updatedAt) => {
  if (!updatedAt) return { label: 'Unknown', color: 'text-gray-400', dotColor: '#9CA3AF', borderColor: '#9CA3AF' };
  const diffSec = Math.floor((Date.now() - new Date(updatedAt).getTime()) / 1000);
  if (diffSec < 120) return { label: 'Active now', color: 'text-green-600', dotColor: '#22C55E', borderColor: '#22C55E' };
  return { label: `${Math.floor(diffSec / 60)}m ago`, color: 'text-amber-500', dotColor: '#F59E0B', borderColor: '#F59E0B' };
};

// Custom marker icon for online users
const createUserMarkerIcon = (userName, isPro, isSelected, updatedAt) => {
  const initials = userName ? userName.charAt(0).toUpperCase() : '?';
  const freshness = getFreshness(updatedAt);
  const bgColor = isSelected ? '#7C3AED' : (isPro ? '#F59E0B' : '#F97316');
  const size = isSelected ? 48 : 40;
  
  return L.divIcon({
    className: 'custom-user-marker',
    html: `
      <div style="position: relative;">
        <div style="
          width: ${size}px;
          height: ${size}px;
          background: ${bgColor};
          border: 3px solid white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: ${isSelected ? '18px' : '14px'};
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          cursor: pointer;
          transition: transform 0.2s;
          ${isSelected ? 'transform: scale(1.1);' : ''}
        ">
          ${initials}
          ${isPro ? '<span style="position: absolute; top: -5px; right: -5px; font-size: 12px;">👑</span>' : ''}
        </div>
        <span style="
          position: absolute; top: -2px; left: -2px;
          width: 12px; height: 12px;
          background: ${freshness.dotColor};
          border: 2px solid white;
          border-radius: 50%;
          box-shadow: 0 1px 3px rgba(0,0,0,0.3);
        "></span>
      </div>
      <div style="
        width: 0;
        height: 0;
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 10px solid ${bgColor};
        margin: -3px auto 0;
      "></div>
    `,
    iconSize: [size, size + 10],
    iconAnchor: [size / 2, size + 10],
    popupAnchor: [0, -size - 5]
  });
};

// Map bounds for United States
const US_BOUNDS = [
  [24.396308, -125.0], // Southwest
  [49.384358, -66.93457] // Northeast
];

const US_CENTER = [39.8283, -98.5795];

// Component to handle map controls
const MapController = ({ selectedUser, onlineUsers }) => {
  const map = useMap();
  
  useEffect(() => {
    if (selectedUser) {
      map.flyTo([selectedUser.latitude, selectedUser.longitude], 15, {
        duration: 1.5
      });
    } else if (onlineUsers.length > 0) {
      // Fit all markers
      const bounds = onlineUsers.map(u => [u.latitude, u.longitude]);
      if (bounds.length > 0) {
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
      }
    }
  }, [selectedUser, map]);
  
  return null;
};

const AdminLiveMap = () => {
  const { getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const mapRef = useRef(null);
  const refreshIntervalRef = useRef(null);

  const fetchOnlineUsers = useCallback(async (showToast = false) => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API}/admin/live-map/online-users`, getAuthHeader());
      setOnlineUsers(response.data.online_users || []);
      setLastUpdated(new Date());
      if (showToast) {
        toast.success(`Found ${response.data.total_count} online users`);
      }
    } catch (error) {
      console.error('Error fetching online users:', error);
      if (showToast) {
        toast.error('Failed to fetch online users');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchOnlineUsers();
    
    // Auto-refresh every 30 seconds
    refreshIntervalRef.current = setInterval(() => {
      fetchOnlineUsers();
    }, 30000);
    
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [fetchOnlineUsers]);

  const handleMarkerClick = (user) => {
    setSelectedUser(user);
  };

  const openDirections = (user, platform) => {
    const { latitude, longitude } = user;
    let url;
    
    if (platform === 'google') {
      url = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
    } else if (platform === 'apple') {
      url = `http://maps.apple.com/?daddr=${latitude},${longitude}&dirflg=d`;
    } else {
      // Waze
      url = `https://waze.com/ul?ll=${latitude},${longitude}&navigate=yes`;
    }
    
    window.open(url, '_blank');
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return date.toLocaleTimeString();
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center h-[calc(100vh-100px)]">
        <div className="text-center">
          <div className="animate-spin w-12 h-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-500">Loading live map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-gray-100' : 'p-6'}`}>
      {/* Header */}
      <div className={`flex items-center justify-between mb-4 ${isFullscreen ? 'px-6 pt-4' : ''}`}>
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <MapPin className="text-purple-600" />
            Live Map
          </h1>
          <Badge className="bg-green-100 text-green-700 flex items-center gap-1">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            {onlineUsers.length} Online
          </Badge>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdated && (
            <span className="text-sm text-gray-500">
              Updated: {lastUpdated.toLocaleTimeString()}
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchOnlineUsers(true)}
            disabled={refreshing}
            className="flex items-center gap-2"
          >
            <RefreshCw size={16} className={refreshing ? 'animate-spin' : ''} />
            Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleFullscreen}
            className="flex items-center gap-2"
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
            {isFullscreen ? 'Exit' : 'Fullscreen'}
          </Button>
        </div>
      </div>

      <div className={`flex gap-4 ${isFullscreen ? 'px-6 h-[calc(100vh-100px)]' : 'h-[calc(100vh-220px)]'}`}>
        {/* Map Container */}
        <div className={`${selectedUser ? 'flex-1' : 'w-full'} relative rounded-xl overflow-hidden shadow-lg border`}>
          <MapContainer
            center={US_CENTER}
            zoom={4}
            style={{ height: '100%', width: '100%' }}
            zoomControl={false}
            ref={mapRef}
            maxBounds={[[-90, -180], [90, 180]]}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            <ZoomControl position="topright" />
            
            <MapController selectedUser={selectedUser} onlineUsers={onlineUsers} />
            
            {/* User Markers */}
            {onlineUsers.map((user) => (
              <React.Fragment key={user.user_id}>
                {/* Trail polyline */}
                {user.route_path && user.route_path.length > 1 && (
                  <Polyline
                    positions={user.route_path.map(p => [p.lat, p.lng])}
                    color={selectedUser?.user_id === user.user_id ? '#7C3AED' : '#F97316'}
                    weight={3}
                    opacity={0.6}
                    dashArray="5,10"
                  />
                )}
                
                {/* User marker */}
                <Marker
                  position={[user.latitude, user.longitude]}
                  icon={createUserMarkerIcon(user.user_name, user.is_pro, selectedUser?.user_id === user.user_id, user.updated_at)}
                  eventHandlers={{
                    click: () => handleMarkerClick(user)
                  }}
                >
                  <Popup>
                    <div className="p-2 min-w-[200px]">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center relative">
                          <span className="text-lg font-bold text-orange-600">
                            {user.user_name.charAt(0)}
                          </span>
                          <span className={`absolute -top-0.5 -left-0.5 w-3 h-3 rounded-full border-2 border-white`} style={{ background: getFreshness(user.updated_at).dotColor }}></span>
                        </div>
                        <div>
                          <p className="font-semibold">{user.user_name}</p>
                          <p className="text-xs text-gray-500">{user.job_title}</p>
                        </div>
                        {user.is_pro && (
                          <span className="text-yellow-500">👑</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-600 space-y-1">
                        <p>📦 {user.deliveries_count} / {user.total_deliveries} deliveries</p>
                        <p className={getFreshness(user.updated_at).color}>⬤ {getFreshness(user.updated_at).label}</p>
                      </div>
                      <div className="mt-3 flex gap-2">
                        <button
                          onClick={() => openDirections(user, 'google')}
                          className="flex-1 bg-blue-500 text-white text-xs py-1.5 px-2 rounded hover:bg-blue-600 transition-colors"
                        >
                          Google Maps
                        </button>
                        <button
                          onClick={() => openDirections(user, 'apple')}
                          className="flex-1 bg-gray-700 text-white text-xs py-1.5 px-2 rounded hover:bg-gray-800 transition-colors"
                        >
                          Apple Maps
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              </React.Fragment>
            ))}
          </MapContainer>
          
          {/* Empty state overlay */}
          {onlineUsers.length === 0 && (
            <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
              <div className="text-center">
                <Users size={48} className="mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-semibold text-gray-600 mb-2">No Users Online</h3>
                <p className="text-gray-500">Users will appear here when they start tracking deliveries</p>
              </div>
            </div>
          )}
        </div>

        {/* Side Panel - User Details */}
        {selectedUser && (
          <Card className="w-96 border-0 shadow-lg overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold">{selectedUser.user_name.charAt(0)}</span>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold flex items-center gap-2">
                      {selectedUser.user_name}
                      {selectedUser.is_pro && <span>👑</span>}
                    </h2>
                    <p className="text-purple-200 text-sm">{selectedUser.job_title}</p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="text-white/60 hover:text-white"
                >
                  ✕
                </button>
              </div>
            </div>
            
            <div className="p-4 flex-1 overflow-y-auto">
              {/* Contact Info */}
              <div className="space-y-3 mb-6">
                <div className="flex items-center gap-3 text-gray-600">
                  <Mail size={16} className="text-gray-400" />
                  <span className="text-sm">{selectedUser.user_email}</span>
                </div>
                {selectedUser.user_phone && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <Phone size={16} className="text-gray-400" />
                    <a href={`tel:${selectedUser.user_phone}`} className="text-sm text-blue-600 hover:underline">
                      {selectedUser.user_phone}
                    </a>
                  </div>
                )}
                <div className="flex items-center gap-3 text-gray-600">
                  <Briefcase size={16} className="text-gray-400" />
                  <span className="text-sm">{selectedUser.client || 'No client'}</span>
                </div>
                {selectedUser.star_rating && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <Star size={16} className="text-yellow-500" />
                    <span className="text-sm">{selectedUser.star_rating.toFixed(1)} rating</span>
                  </div>
                )}
              </div>

              {/* Current Map */}
              <div className="bg-purple-50 border border-purple-100 rounded-lg p-4 mb-6" data-testid="live-map-current-map">
                <h3 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <MapPin size={16} className="text-purple-500" />
                  Current Map
                </h3>
                <p className="text-sm font-medium text-gray-900 mb-2" data-testid="live-map-current-map-name">
                  {selectedUser.job_title || 'Unknown Job'}
                </p>
                {selectedUser.job_id && (
                  <button
                    onClick={() => navigate(`/admin/client-report/${selectedUser.job_id}`)}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-700 hover:text-purple-900 hover:underline"
                    data-testid="live-map-view-delivery-map-link"
                  >
                    <ExternalLink size={14} /> View Delivery Map
                  </button>
                )}
              </div>

              {/* Delivery Progress */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Target size={16} className="text-orange-500" />
                  Delivery Progress
                </h3>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl font-bold text-gray-900">
                    {selectedUser.deliveries_count}
                  </span>
                  <span className="text-gray-500">/ {selectedUser.total_deliveries}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div 
                    className="bg-orange-500 h-2.5 rounded-full transition-all"
                    style={{ width: `${Math.min((selectedUser.deliveries_count / selectedUser.total_deliveries) * 100, 100)}%` }}
                  ></div>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {Math.round((selectedUser.deliveries_count / selectedUser.total_deliveries) * 100)}% complete
                </p>
              </div>

              {/* Location Info */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <MapPin size={16} className="text-purple-500" />
                  Current Location
                </h3>
                <div className="text-sm text-gray-600 space-y-2">
                  <p>Lat: {selectedUser.latitude.toFixed(6)}</p>
                  <p>Lng: {selectedUser.longitude.toFixed(6)}</p>
                  {selectedUser.speed !== undefined && (
                    <p>Speed: {(selectedUser.speed * 2.237).toFixed(1)} mph</p>
                  )}
                  {selectedUser.accuracy && (
                    <p>Accuracy: ±{selectedUser.accuracy.toFixed(0)}m</p>
                  )}
                </div>
                <div className={`flex items-center gap-2 mt-3 text-xs ${getFreshness(selectedUser.updated_at).color}`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: getFreshness(selectedUser.updated_at).dotColor }}></span>
                  {getFreshness(selectedUser.updated_at).label}
                </div>
              </div>

              {/* Navigation Buttons */}
              <div className="space-y-2">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Navigation size={16} className="text-blue-500" />
                  Get Directions
                </h3>
                <Button
                  onClick={() => openDirections(selectedUser, 'google')}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center gap-2"
                >
                  <ExternalLink size={16} />
                  Open in Google Maps
                </Button>
                <Button
                  onClick={() => openDirections(selectedUser, 'apple')}
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2"
                >
                  <ExternalLink size={16} />
                  Open in Apple Maps
                </Button>
                <Button
                  onClick={() => openDirections(selectedUser, 'waze')}
                  variant="outline"
                  className="w-full flex items-center justify-center gap-2 text-cyan-600 border-cyan-200 hover:bg-cyan-50"
                >
                  <Zap size={16} />
                  Open in Waze
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Online Users List (when no user selected) */}
      {!selectedUser && onlineUsers.length > 0 && (
        <div className={`mt-4 ${isFullscreen ? 'px-6 pb-4' : ''}`}>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">Online Users</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {onlineUsers.map((user) => (
              <Card 
                key={user.user_id}
                className="p-4 border-0 shadow-sm hover:shadow-md cursor-pointer transition-all"
                onClick={() => handleMarkerClick(user)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center relative">
                      <span className="font-bold text-orange-600">
                        {user.user_name.charAt(0)}
                      </span>
                      <span className="absolute -top-0.5 -left-0.5 w-3 h-3 rounded-full border-2 border-white" style={{ background: getFreshness(user.updated_at).dotColor }}></span>
                      {user.is_pro && (
                        <span className="absolute -top-1 -right-1 text-xs">👑</span>
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{user.user_name}</p>
                      <p className="text-xs text-gray-500">{user.job_title}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold text-orange-600">
                      {user.deliveries_count}/{user.total_deliveries}
                    </p>
                    <p className={`text-xs ${getFreshness(user.updated_at).color}`}>{getFreshness(user.updated_at).label}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLiveMap;
